import React, { Dispatch, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui/components/ui/button';
import { togglerTabButtonClass } from '@app/components/core/toggler-tab-button-class';
import { runViewTransition } from '@app/components/core/viewTransition';
import { membersReportingListPath, sprintReportingPath } from '@app/constants/url';
import { SprintReportTab } from '@app/utils/types';

/** Timesheets answers three questions: what you owe, what you filed, and what the team filed. */
export type TimesheetsView = SprintReportTab | 'members';

/** Two views are tabs of one page and the third a page of its own, so a press moves a tab or navigates. */
export const TimesheetsToggler = ({
  active,
  setTab,
  organizationId,
  canSeeMembers,
}: {
  active: TimesheetsView;
  /** Given by the page that owns the tab state; absent on the members page, which navigates back to it. */
  setTab?: Dispatch<SprintReportTab>;
  organizationId: string;
  /** Hides an option the viewer would be redirected out of; withAuth on the route is the real guard. */
  canSeeMembers: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Cross-fade either way: all three are the same level, so none of them is a layer deeper.
  const go = (to: TimesheetsView, move: () => void): void => {
    if (to === active) return;
    runViewTransition('fade', move);
  };

  const showTab = (tab: SprintReportTab): void =>
    go(tab, () => {
      if (!setTab) {
        navigate(`${sprintReportingPath(organizationId)}?tab=${tab}`);
        return;
      }
      setTab(tab);
      // A tab is view state, not a step: pushing one per click made the detail pages' back control walk tabs.
      navigate({ search: `?tab=${tab}` }, { replace: true });
    });

  return (
    <div
      className="no-scrollbar inline-flex max-w-full items-stretch overflow-x-auto rounded-md bg-card p-1"
      data-slot="button-group"
      role="group"
    >
      {[SprintReportTab.toReport, SprintReportTab.completed].map(tab => (
        <Button key={tab} className={togglerTabButtonClass(active === tab)} onClick={() => showTab(tab)}>
          {t(`tabs.${tab}`)}
        </Button>
      ))}
      {canSeeMembers && (
        <Button
          className={togglerTabButtonClass(active === 'members')}
          onClick={() => go('members', () => navigate(membersReportingListPath(organizationId)))}
        >
          {t('tabs.members')}
        </Button>
      )}
    </div>
  );
};
